import { useEffect, useState } from "react";
import Logo from "@/components/tpr/Logo/Logo";
import RandomWallpaper from "@/components/tpr/RandomWallpaper/RandomWallpaper";
import TprIcon from "@/components/tpr/TprIcon/TprIcon";
import SetPasswordFields from "@/components/tpr/SetPasswordFields/SetPasswordFields";
import { Button } from "@/components/ui/button";
import { Field, FieldGroup, FieldSet } from "@/components/ui/field";
import { Item, ItemContent } from "@/components/ui/item";
import { Spinner } from "@/components/ui/spinner";
import { gaPageView } from "@/core/google";
import { useCompletePasswordReset } from "@/hooks/useCompletePasswordReset";
import { useNavigate, useSearchParams } from "react-router";

const ResetPasswordView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationError, setValidationError] = useState("");
  const [token, setToken] = useState<string | null>(null);

  const { mutate: completeReset, isPending, error } = useCompletePasswordReset();

  useEffect(() => {
    gaPageView("/reset-password");
  }, []);

  useEffect(() => {
    const tokenParam = searchParams.get("token");
    const errorParam = searchParams.get("error");

    if (errorParam === "INVALID_TOKEN") {
      setValidationError("This password reset link is invalid or has expired.");
      return;
    }

    if (!tokenParam) {
      setValidationError("No reset token found. Please request a new password reset link.");
      return;
    }

    setToken(tokenParam);
  }, [searchParams]);

  const validatePassword = (password: string): boolean => {
    return password.length >= 8;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError("");

    if (!token) {
      setValidationError("No reset token found. Please request a new password reset link.");
      return;
    }

    if (!validatePassword(newPassword)) {
      setValidationError("Password must be at least 8 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError("Passwords do not match");
      return;
    }

    completeReset(
      {
        newPassword,
        token,
      },
      {
        onSuccess: () => {
          navigate("/login?resetSuccess=true", { replace: true });
        },
        onError: (err) => {
          if (err.message.includes("expired") || err.message.includes("invalid")) {
            setValidationError("This password reset link has expired or is invalid. Please request a new one.");
          }
        },
      },
    );
  };

  const renderError = () => {
    return (
      <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
        <ItemContent>
          <div className="flex flex-col gap-4">
            <div
              className="p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
              role="alert"
            >
              <p className="font-semibold">Invalid or Expired Link</p>
              <p className="mt-2">{validationError}</p>
            </div>
            <Button
              variant="secondary"
              onClick={() => navigate("/forgot-password")}
              aria-label="Request new reset link"
            >
              Request new reset link
            </Button>
          </div>
        </ItemContent>
      </Item>
    );
  };

  const renderForm = () => {
    return (
      <Item className="border border-gray-200 bg-gray-50 dark:border-gray-700 rounded-lg p-4">
        <ItemContent>
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-2">
              {validationError && (
                <div
                  className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                  role="alert"
                >
                  {validationError}
                </div>
              )}
              {error && (
                <div
                  className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                  role="alert"
                >
                  Failed to reset password. Please try again.
                </div>
              )}
              <FieldSet>
                <FieldGroup>
                  <SetPasswordFields
                    password={newPassword}
                    confirm={confirmPassword}
                    onPasswordChange={setNewPassword}
                    onConfirmChange={setConfirmPassword}
                  />
                  <Field orientation="horizontal">
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={!newPassword || !confirmPassword || isPending}
                      aria-label="Reset password"
                    >
                      {isPending ? <Spinner className="h-4 w-4 mr-2" /> : null}
                      Reset password
                    </Button>
                  </Field>
                </FieldGroup>
              </FieldSet>
            </div>
          </form>
        </ItemContent>
      </Item>
    );
  };

  if (isPending) {
    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="flex justify-center h-screen">
          <RandomWallpaper />
          <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-3/6">
            <div className="flex-1">
              <div className="text-center flex flex-col justify-center items-center">
                <div className="mb-4">
                  <TprIcon size="large" />
                </div>
                <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                  <Logo color="black" size="large" />
                </h2>
              </div>
              <div className="flex justify-center items-center flex-col gap-6 mt-8">
                <Spinner className="h-12 w-12" />
                <span>Resetting your password...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="flex justify-center h-screen">
        <RandomWallpaper />

        <div className="flex items-center w-full max-w-md px-6 mx-auto lg:w-3/6">
          <div className="flex-1">
            <div className="text-center flex flex-col justify-center items-center">
              <div className="mb-4">
                <TprIcon size="large" />
              </div>
              <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                <Logo color="black" size="large" />
              </h2>
              <p className="mt-3 text-gray-500 dark:text-gray-300">Create a new password</p>
            </div>

            <div className="mt-8 w-full">{!token || validationError ? renderError() : renderForm()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordView;
